import { HeightMode } from '@wix/ambassador-app-service-webapp/types';
import {
  WidgetSize,
  DefaultPresetKeys,
  PresetsOptions,
} from '../../../../types/unifiedComponents';
import {
  InitialSizeSetting,
  ComponentInitialSize,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import { CompLayout, Point, UnitSize } from '@wix/document-services-types';
import { Item } from '@wix/editor-platform-host-integration-apis';

export const chainPromisesAsync = <T>(
  array: T[],
  functionToApply: (item: T) => Promise<any>,
) =>
  array.reduce(
    (previousPromise, currentItem) =>
      previousPromise.then(() => functionToApply(currentItem)),
    Promise.resolve(),
  );

export const getSize = (size?: WidgetSize) => ({
  height:
    size?.height?.heightMode === HeightMode.FIXED && size?.height?.defaultHeight
      ? size?.height?.defaultHeight
      : 500,
  width: size?.width?.defaultWidth ?? 500,
  ...(size?.width?.stretchByDefault
    ? {
        docked: {
          left: { px: 0, vw: 0 },
          right: { px: 0, vw: 0 },
        },
      }
    : {}),
});

export const cleanPresetIdPrefix = (presetId: string) =>
  presetId.replace(/^#+/g, '');

export const getSizeValue = (unitSize: UnitSize | number): number =>
  typeof unitSize === 'number' ? unitSize : unitSize.value;

export const getUnitSize = (
  sizeValue: UnitSize | number,
): UnitSize & { type: 'px' } =>
  typeof sizeValue !== 'number'
    ? { ...sizeValue, type: 'px' }
    : { value: sizeValue, type: 'px' };

export function initialSizeSettingToUnitSize(
  setting: InitialSizeSetting | undefined,
): UnitSize | undefined {
  if (!setting) {
    return undefined;
  }

  switch (setting.sizingType) {
    case 'pixels':
      return { type: 'px', value: setting.pixels ?? 0 };
    case 'content':
      // No 'fitContent' type, fallback to 'px' with a default value (e.g., 100)
      return { type: 'px', value: setting.pixels ?? 100 };
    case 'stretched':
      return { type: 'percentage', value: 100 };
    default:
      return undefined;
  }
}

/**
 * Converts a ComponentInitialSize to { width: UnitSize, height: UnitSize }
 * Falls back to provided defaults if not set.
 */
export function componentInitialSizeToUnitSize(
  initialSize: ComponentInitialSize | undefined,
  fallback: { width: UnitSize; height: UnitSize },
): { width: UnitSize; height: UnitSize } {
  return {
    width: initialSizeSettingToUnitSize(initialSize?.width) ?? fallback.width,
    height:
      initialSizeSettingToUnitSize(initialSize?.height) ?? fallback.height,
  };
}

export const getFixedPosition = (
  containerLayout: CompLayout | null,
  widgetSize: Item['size'],
  position?: Point,
) => {
  if (position) {
    return position;
  }

  const isStretched = widgetSize.docked !== undefined;

  if (containerLayout !== null && !isStretched) {
    return {
      x: Math.max((containerLayout.width - widgetSize.width) / 2, 0),
      y: 0,
    } as Point;
  }

  return { x: 0, y: 0 } as Point;
};

export const getInstallationPresetIdByBreakpoint = ({
  widgetDefinition,
  presetIdKey = DefaultPresetKeys.LARGE,
  overridePreset,
  shouldReadDataFromNewSchema,
}: PresetsOptions): string | undefined => {
  const deprecatedPresetIdKey =
    presetIdKey === DefaultPresetKeys.SMALL
      ? 'mobilePresetId'
      : 'desktopPresetId';

  const breakpointPresetId =
    presetIdKey === DefaultPresetKeys.SMALL
      ? widgetDefinition.presets?.[0]?.breakpoints?.small?.presetId
      : widgetDefinition.presets?.[0]?.id;

  const deprecatedPreset =
    widgetDefinition.installation?.widget?.defaultPreset?.[
      deprecatedPresetIdKey
    ];

  const installationPreset = shouldReadDataFromNewSchema
    ? widgetDefinition.installation?.presets?.[presetIdKey] ?? deprecatedPreset
    : deprecatedPreset;

  return (
    overridePreset?.[deprecatedPresetIdKey] ??
    installationPreset ??
    breakpointPresetId
  );
};
